import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Avatar,
  Stack,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import { getUserInfo } from "../apis/apis";
import { useNavigate } from 'react-router-dom';
import { uploadImageUsingFile, setUserSettings } from '../apis/apis';
import Navbar from '../components/Navbar';


const Settings = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userInfo = await getUserInfo();
        const transformedUser = {
          ...userInfo,
          profilePhoto: userInfo.profilePhotoUrl ? {
            preview: userInfo.profilePhotoUrl,
            file: null,
            isExisting: true
          } : null
        };
        setUser(transformedUser);
      } catch (error) {
        if (error.message.includes("403")) {
          navigate('/verify_email');
        } else if (error.message.includes("400")) {
          navigate('/signin');
        } else if (error.message.includes("401")) {
            navigate('/signin');
        } 
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, [navigate]);

  const handlePhotoChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file)
      setUser((oldUser) => ({ ...oldUser, profilePhoto: {preview: url, file: file, isExisting: false} }));
    }
  };

  const handleClearPhoto = () => {
    setUser((oldUser) => ({ ...oldUser, profilePhoto: null }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSaving(true);

    try {
      let imageUrl = user?.profilePhoto?.preview
      if (user?.profilePhoto && !user.profilePhoto.isExisting) {
        const fileResponse = await uploadImageUsingFile(user.profilePhoto.file);
        imageUrl = fileResponse.url;
        setUser((oldUser) => ({ ...oldUser, profilePhoto: {preview: fileResponse.url, file: null, isExisting: true } }));
      }

      const userInfo = await setUserSettings(user.name, imageUrl);
      const transformedUser = {
        ...userInfo,
        profilePhoto: userInfo.profilePhotoUrl ? {
          preview: userInfo.profilePhotoUrl,
          file: null,
          isExisting: true
        } : null
      };
      setUser(transformedUser);
      setShowSuccessAlert(true);
    } finally {
      setIsSaving(false);
    }

  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 6, p: 4, boxShadow: 3, borderRadius: 2, bgcolor: 'background.paper', marginTop: '96px' }}>
        <Typography variant="h4" sx={{ mb: 4, textAlign: 'center', fontWeight: 'bold' }}>Settings</Typography>
        <form onSubmit={async (event) => await handleSubmit(event)}>
          <Box sx={{ display: 'flex', gap: 4 }}>
            {/* Left side - Photo section */}
            <Box sx={{ flex: '0 0 200px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Avatar 
                src={user?.profilePhoto?.preview} 
                sx={{ 
                  width: 200, 
                  height: 200, 
                  boxShadow: 2,
                  mb: 2
                }} 
              />
              <Stack direction="row" spacing={2}>
                <Button 
                variant="outlined" 
                component="label" 
                fullWidth
                sx={{ textTransform: 'none' }}
                >
                Change
                <input type="file" hidden onChange={handlePhotoChange} accept="image/*" />
                </Button>
                <Button 
                variant="outlined" 
                component="label" 
                fullWidth
                sx={{ textTransform: 'none' }}
                onClick={handleClearPhoto}
                >
                Clear
                </Button>
              </Stack>
            </Box>

            {/* Right side - Form fields */}
            <Box sx={{ flex: 1 }}>
              <TextField
                label="Name"
                fullWidth
                value={user?.name || ''}
                onChange={(e) => setUser((prevUser) => ({ ...prevUser, name: e.target.value }))}
                margin="normal"
                sx={{ mb: 2 }}
              />
              <TextField
                label="Email"
                fullWidth
                value={user?.email || ''}
                margin="normal"
                disabled
                sx={{ mb: 3 }}
              />
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                fullWidth 
                disabled={isSaving}
                sx={{ py: 1.5 }}
              >
                {isSaving ? <CircularProgress size={24} color="inherit" /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </form>
        <Snackbar
          open={showSuccessAlert}
          autoHideDuration={3000}
          onClose={() => setShowSuccessAlert(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setShowSuccessAlert(false)} severity="success">
            Settings changed successfully
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default Settings;